import { useEffect } from "react";
import { client } from "@/api/client";
import { useMerchant } from "@/providers/MerchantProvider";

export type RefreshableType = "collab";

/**
 * Stale-while-revalidate: requests a server-side refresh of the given resource(s) on mount.
 * Fire-and-forget — the server gates on a cooldown and deduplicates concurrent calls.
 * When a refresh completes, the SSE provider catches `resource_refreshed` and invalidates
 * the relevant queries; this hook does not need to handle the response.
 */
export function useFreshen(type: RefreshableType, ids: string[]) {
  const merchantId = useMerchant();
  const idsKey = ids.join(",");

  useEffect(() => {
    if (!merchantId || ids.length === 0) return;

    if (type === "collab") {
      for (const collaborationId of ids) {
        client.merchant[":merchantId"].collaborations.target[":collaborationId"].refresh
          .$post({ param: { merchantId, collaborationId } })
          .catch(() => {
            // Best-effort: refresh failures are transient and the user will see fresh
            // data on the next page load (or when the cooldown expires and another
            // refresh succeeds).
          });
      }
    }
    // idsKey is the stable dependency; ids array identity changes on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, type, idsKey]);
}

/**
 * Stale-while-revalidate for the target-collab list view. Triggers a single server-side
 * walk of the merchant's TT collabs, short-circuited at the per-row update_time watermark.
 * SSE drives invalidation just like `useFreshen` — see ResourceRefreshProvider.
 */
export function useFreshenCollaborationList() {
  const merchantId = useMerchant();

  useEffect(() => {
    if (!merchantId) return;

    client.merchant[":merchantId"].collaborations.target.refresh.$post({ param: { merchantId } }).catch(() => {
      // Best-effort — same rationale as useFreshen.
    });
  }, [merchantId]);
}
